import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FeeBreakdown from '@/components/FeeBreakdown';
import HistoricalChart from '@/components/HistoricalChart';
import FeeRadarChart from '@/components/FeeRadarChart';
import CountyComparisonChart from '@/components/CountyComparisonChart';
import ComparisonSelector from '@/components/ComparisonSelector';
import { getMunicipalityById, getAllMunicipalities, Municipality, getFeesForYear, getMunicipalityRankingForYear, filterFees, getYearlyTotal } from '@/lib/data';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';
import { generateSSBLinkForMunicipality } from '@/services/ssbService';
import { getWikipediaUrl } from '@/lib/utils';
import { ArrowLeft, MapPin, Users, AlertTriangle, ExternalLink, Database, BookOpen, Trophy, Medal, BarChart4, TrendingUp, TrendingDown } from 'lucide-react';
import { getYearRange, getLatestYear } from '@/config/years';
import YearSelector from '@/components/YearSelector';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const MunicipalityDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enabledCategories } = useFeeCategories();
  const [municipality, setMunicipality] = useState<Municipality | null>(null);
  const [allMunicipalities, setAllMunicipalities] = useState<Municipality[]>([]);
  const [compareMunicipalities, setCompareMunicipalities] = useState<Municipality[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(() => getLatestYear());
  const [isLoading, setIsLoading] = useState(true);
  const [hasLimitedData, setHasLimitedData] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const [data, all] = await Promise.all([
          getMunicipalityById(id),
          getAllMunicipalities()
        ]);
        
        if (!data) {
          toast.error("Kommune ikke funnet");
          navigate('/');
          return;
        }
        
        setMunicipality(data);
        setAllMunicipalities(all);
        
        // Sjekk om vi har begrenset historisk data (vises i Alert under, ingen toast)
        if (data.historicalData.length <= 1) {
          setHasLimitedData(true);
        } else {
          const uniqueYears = new Set(data.historicalData.map(item => item.year)).size;
          if (uniqueYears <= 1) {
            setHasLimitedData(true);
          }
        }
      } catch (error) {
        console.error('Error fetching municipality data:', error);
        toast.error("Kunne ikke hente kommunedata");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);
  
  const handleMunicipalitySelect = (selected: Municipality) => {
    if (compareMunicipalities.length >= 5) {
      toast.warning("Du kan maksimalt sammenligne 5 kommuner samtidig");
      return;
    }
    
    setCompareMunicipalities([...compareMunicipalities, selected]);
  };
  
  const handleMunicipalityRemove = (municipalityId: string) => {
    setCompareMunicipalities(
      compareMunicipalities.filter(m => m.id !== municipalityId)
    );
  };
  
  const yearData = municipality ? filterFees(getFeesForYear(municipality, selectedYear), enabledCategories) : [];
  const totalFees = yearData.reduce((sum, f) => sum + f.amount, 0);

  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      {municipality ? (
        <Seo
          title={`${municipality.name} – kommunale avgifter ${selectedYear} | Kommunale avgifter`}
          description={`${municipality.name} (${municipality.county}): Total ${totalFees.toLocaleString('nb-NO')} kr/år i kommunale avgifter for en standardbolig. Vann, avløp, renovasjon, septiktømming.`}
          path={`/municipality/${id}`}
        />
      ) : (
        <Seo title="Kommune | Kommunale avgifter i Norge" description="Last inn kommunedata…" path={id ? `/municipality/${id}` : '/'} />
      )}
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {isLoading ? (
          <>
            <nav className="flex items-center gap-2 text-sm text-warmGray mb-6">
              <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" />
                Forside
              </Link>
              <span>/</span>
              <span className="text-deepCharcoal">…</span>
            </nav>
            <div className="mb-8">
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-5 w-48" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Skeleton className="h-80" />
              <Skeleton className="h-80" />
            </div>
          </>
        ) : municipality ? (
          <>
            {/* Hero-seksjon */}
            <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
              <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
                <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" />
                Forside
              </Link>
                <span>/</span>
                <span className="text-deepCharcoal font-medium">{municipality.county}</span>
                <span>/</span>
                <span className="text-nordicBlue font-semibold">{municipality.name}</span>
              </nav>
              <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">{municipality.name}</h1>
                  <div className="flex flex-wrap gap-4 text-warmGray">
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1.5 text-nordicBlue" />
                      <span>{municipality.county}</span>
                    </div>
                    {municipality.population > 0 && (
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1.5 text-nordicBlue" />
                        <span>{municipality.population.toLocaleString('nb-NO')} innbyggere</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <YearSelector value={selectedYear} onChange={setSelectedYear} />
                  <a 
                    href={getWikipediaUrl(municipality.name)}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-warmGray hover:text-nordicBlue transition-colors"
                    title="Les om kommunen på Wikipedia"
                  >
                    <BookOpen className="h-4 w-4 mr-1" />
                    <span>Om kommunen</span>
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  {id && (
                    <a 
                      href={generateSSBLinkForMunicipality(id)}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-nordicBlue hover:text-nordicBlue/80 transition-colors"
                      title="Se rådata i SSB"
                    >
                      <Database className="h-4 w-4 mr-1" />
                      <span>Data i SSB</span>
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Hovedtall-kort – samme stil som forsiden */}
            {(() => {
              const fees = filterFees(getFeesForYear(municipality, selectedYear), enabledCategories);
              const totalFees = fees.reduce((sum, f) => sum + f.amount, 0);
              const ranking = allMunicipalities.length > 0
                ? getMunicipalityRankingForYear(municipality, allMunicipalities, selectedYear, enabledCategories)
                : null;
              const withYear = allMunicipalities.filter(m => m.historicalData.some(d => d.year === selectedYear));
              const avgTotal = withYear.length > 0
                ? withYear.reduce((sum, m) => {
                    const yd = m.historicalData.find(d => d.year === selectedYear);
                    return sum + (yd ? getYearlyTotal(yd, enabledCategories) : 0);
                  }, 0) / withYear.length
                : 0;
              const pctFromAvg = avgTotal > 0 ? ((totalFees - avgTotal) / avgTotal) * 100 : null;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
                    <div className="rounded-xl bg-slate-50 p-3 shrink-0">
                      <BarChart4 className="h-6 w-6 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Totale avgifter ({selectedYear})</p>
                      <p className="text-base font-semibold text-deepCharcoal">
                        {totalFees.toLocaleString('nb-NO')} kr/år
                      </p>
                      <p className="text-sm text-warmGray">Standardbolig 120 m²</p>
                    </div>
                  </div>
                  {ranking != null && (
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-amber-100 flex items-center gap-4">
                      <div className="rounded-xl bg-amber-50 p-3 shrink-0">
                        {ranking === 1 ? (
                          <Trophy className="h-6 w-6 text-amber-600" />
                        ) : (
                          <Medal className="h-6 w-6 text-amber-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Plassering</p>
                        <p className="text-base font-semibold text-deepCharcoal">
                          {ranking === 1 ? 'Høyeste i landet' : `#${ranking} av ${allMunicipalities.length}`}
                        </p>
                      </div>
                    </div>
                  )}
                  {pctFromAvg != null && !isNaN(pctFromAvg) && (
                    <div className={`bg-white p-4 rounded-lg shadow-sm border flex items-center gap-4 ${
                      pctFromAvg > 0 ? 'border-red-100' : 'border-slate-100'
                    }`}>
                      <div className={`rounded-xl p-3 shrink-0 ${
                        pctFromAvg > 0 ? 'bg-red-50' : pctFromAvg < 0 ? 'bg-slate-50' : 'bg-slate-50'
                      }`}>
                        {pctFromAvg > 0 ? (
                          <TrendingUp className="h-6 w-6 text-red-600" />
                        ) : pctFromAvg < 0 ? (
                          <TrendingDown className="h-6 w-6 text-slate-600" />
                        ) : (
                          <Medal className="h-6 w-6 text-slate-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Vs. lands snitt</p>
                        <p className={`text-base font-semibold ${
                          pctFromAvg > 0 ? 'text-red-700' : pctFromAvg < 0 ? 'text-slate-700' : 'text-deepCharcoal'
                        }`}>
                          {pctFromAvg > 0 ? '+' : ''}{pctFromAvg.toFixed(1)}% {pctFromAvg > 0 ? 'over' : pctFromAvg < 0 ? 'under' : 'lik'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {hasLimitedData && (
              <Alert className="mb-6 border-amber-400 bg-amber-50 text-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle>Begrenset datamateriale</AlertTitle>
                <AlertDescription>
                  For denne kommunen har vi begrenset historisk data. Statistikken kan inneholde estimerte verdier 
                  basert på tilgjengelige data for å muliggjøre visualisering.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <FeeBreakdown municipality={municipality} selectedYear={selectedYear} />
                <HistoricalChart 
                  municipality={municipality} 
                  compareMunicipalities={compareMunicipalities} 
                />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <FeeRadarChart
                  municipality={municipality}
                  allMunicipalities={allMunicipalities}
                  selectedYear={selectedYear}
                />
                <CountyComparisonChart
                  municipality={municipality}
                  allMunicipalities={allMunicipalities}
                  selectedYear={selectedYear}
                />
              </div>
              <div>
                <ComparisonSelector 
                  selectedMunicipalities={compareMunicipalities}
                  onMunicipalitySelect={handleMunicipalitySelect}
                  onMunicipalityRemove={handleMunicipalityRemove}
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Du kan søke blant alle norske kommuner for å sammenligne gebyrer og historisk utvikling
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <h2 className="text-2xl font-semibold mb-4">Kommune ikke funnet</h2>
            <Button onClick={() => navigate('/')}>Gå til forsiden</Button>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default MunicipalityDetail;
